import { Alert, Skeleton } from '@mui/material';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { CustomChartRangeSelection } from '../../../common/model/type/chart-range-selection.type';
import { OvulatoryEventLoadStatus } from '../../../common/model/type/ovulatory-event-prediction.type';
import {
  buildOvulatoryEventTableDays,
  formatOvulatoryEventDay,
  formatOvulatoryEventDayRange,
  getDefaultOvulatoryEventDateRange,
  getOvulatoryEventLastPageOffset,
  getOvulatoryEventPageDayRange,
  joinOvulatoryEventDayRows,
  OVULATORY_EVENT_DEFAULT_RANGE_DAYS,
  OVULATORY_EVENT_PAGE_SIZE,
  toOvulatoryEventDayRangeQuery,
  toOvulatoryEventPageOffset,
  toOvulatoryEventScorecard,
} from '../../../common/utils/services/ovulatory-event-prediction-utils';
import DateUtils from '../../../common/utils/services/date-utils';
import usePaginationQueryParams from '../../../hooks/use-pagination-query-params';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';
import {
  getOvulatoryEventComparison,
  getOvulatoryEventPredictions,
  getOvulatoryEventScorecard,
} from '../../../services/admin/admin-ovulatory-event-prediction.service';
import OvOvulatoryEventEmptyState from '../molecules/OvOvulatoryEventEmptyState';
import OvOvulatoryEventScorecard from '../molecules/OvOvulatoryEventScorecard';
import OvOvulatoryEventSurface from '../molecules/OvOvulatoryEventSurface';
import OvUserChartsDaterangeSelector from '../molecules/OvUserChartsDaterangeSelector';
import OvOvulatoryEventDayTable from './OvOvulatoryEventDayTable';
import { DailyDataRange, FetchDailyData } from './OvUserCharts';

interface OvOvulatoryEventComparisonProps {
  userId: string;
  fetchDailyData: FetchDailyData;
  fetchCalendarDailyData: FetchDailyData;
}

const toLoadStatus = ({
  isPending,
  isError,
}: Pick<UseQueryResult, 'isPending' | 'isError'>): OvulatoryEventLoadStatus => {
  if (isPending) {
    return 'loading';
  }

  return isError ? 'error' : 'ready';
};

const SKELETON_LINE_WIDTHS = ['100%', '90%', '80%'];

const OvOvulatoryEventComparison: FC<OvOvulatoryEventComparisonProps> = ({
  userId,
  fetchDailyData,
  fetchCalendarDailyData,
}) => {
  const { t } = useTranslation();
  const { offset, setOffset } = usePaginationQueryParams();
  const [rangeSelection, setRangeSelection] =
    useState<CustomChartRangeSelection>({
      type: 'preset',
      days: OVULATORY_EVENT_DEFAULT_RANGE_DAYS,
    });
  const [calendarRange, setCalendarRange] = useState<DailyDataRange | null>(
    null
  );
  const dateRange =
    rangeSelection.type === 'preset'
      ? getDefaultOvulatoryEventDateRange(new Date(), rangeSelection.days)
      : rangeSelection;
  const range = toOvulatoryEventDayRangeQuery(dateRange);
  const pageOffset = toOvulatoryEventPageOffset(offset);

  const comparisonQuery = useQuery({
    queryKey: [
      'admin-ovulatory-event-comparison',
      userId,
      range.day_from,
      range.day_to,
      pageOffset,
    ],
    queryFn: () =>
      getOvulatoryEventComparison(userId, {
        ...range,
        limit: OVULATORY_EVENT_PAGE_SIZE,
        offset: pageOffset,
        order_dir: 'desc',
      }),
    gcTime: 0,
  });

  const scorecardQuery = useQuery({
    queryKey: [
      'admin-ovulatory-event-scorecard',
      userId,
      range.day_from,
      range.day_to,
    ],
    queryFn: () => getOvulatoryEventScorecard(userId, range),
  });

  const comparison = comparisonQuery.data?.docs ?? [];
  const total = comparisonQuery.data?.total ?? 0;
  const pageRange = getOvulatoryEventPageDayRange(comparison);
  const lastPageOffset = getOvulatoryEventLastPageOffset(total);
  const isPastLastPage =
    comparisonQuery.isSuccess &&
    comparison.length === 0 &&
    pageOffset > lastPageOffset;
  const isLoadingPage = comparisonQuery.isPending || isPastLastPage;
  const isEmptyRange =
    comparisonQuery.isSuccess && !isPastLastPage && total === 0;

  useEffect(() => {
    if (isPastLastPage) {
      setOffset(lastPageOffset);
    }
  }, [isPastLastPage, lastPageOffset, setOffset]);

  const predictionsQuery = useQuery({
    queryKey: [
      'admin-ovulatory-event-predictions',
      userId,
      pageRange?.day_from,
      pageRange?.day_to,
    ],
    queryFn: () =>
      pageRange ? getOvulatoryEventPredictions(userId, pageRange) : [],
    enabled: !!pageRange,
  });

  const scansQuery = useQuery({
    queryKey: [
      'admin-ovulatory-event-daily-data',
      userId,
      pageRange?.day_from,
      pageRange?.day_to,
    ],
    queryFn: () => {
      const startDate = DateUtils.parseDayString(pageRange?.day_from ?? '');
      const endDate = DateUtils.parseDayString(pageRange?.day_to ?? '');

      return startDate && endDate ? fetchDailyData({ startDate, endDate }) : [];
    },
    enabled: !!pageRange,
  });

  const { data: dailyDataInCalendar = [] } = useQuery({
    queryKey: [
      'user-daily-data-range',
      userId,
      calendarRange?.startDate.toISOString(),
      calendarRange?.endDate.toISOString(),
    ],
    queryFn: () => (calendarRange ? fetchCalendarDailyData(calendarRange) : []),
    enabled: !!calendarRange,
  });

  const scorecard = scorecardQuery.data
    ? toOvulatoryEventScorecard(scorecardQuery.data)
    : null;
  const scorecardRows = scorecard
    ? scorecard.counted +
      Object.values(scorecard.excluded).reduce((sum, count) => sum + count, 0)
    : 0;

  const rows = joinOvulatoryEventDayRows(comparison, predictionsQuery.data);

  let status = `${total} scored ${
    total === 1 ? 'day' : 'days'
  } · Member local dates`;

  if (isLoadingPage) {
    status = 'Loading selected range…';
  } else if (comparisonQuery.isError) {
    status = 'Range data unavailable';
  }

  return (
    <Section aria-labelledby="ovulatory-event-history-title">
      <Toolbar>
        <div>
          <Heading>
            <Title id="ovulatory-event-history-title">History</Title>
            <Range>
              {formatOvulatoryEventDayRange(range.day_from, range.day_to)}
            </Range>
          </Heading>
          <Status role="status">{status}</Status>
        </div>
        <RangeSelector>
          <OvUserChartsDaterangeSelector
            selection={rangeSelection}
            defaultText={
              rangeSelection.type === 'preset'
                ? t('dashboard.lastXDays', { numOfDays: rangeSelection.days })
                : t('dashboard.customDateRange')
            }
            dateRange={dateRange}
            onSelectionChange={(selection) => {
              setRangeSelection(selection);
              setOffset(0);
            }}
            dailyDataInCalendar={dailyDataInCalendar}
            onCalendarRangeChange={(startDate, endDate) =>
              setCalendarRange({ startDate, endDate })
            }
            embedded
          />
        </RangeSelector>
      </Toolbar>

      {(scorecardQuery.isPending ||
        scorecardQuery.isError ||
        scorecardRows > 0) && (
        <OvOvulatoryEventSurface>
          {scorecardQuery.isPending && (
            <Skeleton
              variant="rounded"
              height="5.625rem"
              role="status"
              aria-label="Loading scorecard"
            />
          )}
          {scorecardQuery.isError && (
            <OvOvulatoryEventEmptyState
              title="Scorecard could not be loaded"
              tone="error"
            >
              <p>{scorecardQuery.error.message}</p>
            </OvOvulatoryEventEmptyState>
          )}
          {scorecard && (
            <OvOvulatoryEventScorecard
              scorecard={scorecard}
              scope="This member · Selected range"
            />
          )}
        </OvOvulatoryEventSurface>
      )}

      {isLoadingPage && (
        <OvOvulatoryEventSurface aria-hidden="true" data-testid="days-skeleton">
          <Title as="span">Days</Title>
          {SKELETON_LINE_WIDTHS.map((width) => (
            <Skeleton
              key={width}
              variant="rounded"
              height="0.625rem"
              width={width}
            />
          ))}
        </OvOvulatoryEventSurface>
      )}

      {comparisonQuery.isError && (
        <OvOvulatoryEventSurface>
          <OvOvulatoryEventEmptyState
            title="Days could not be loaded"
            tone="error"
          >
            <p>
              The days are unavailable for this request (
              {comparisonQuery.error.message}).
            </p>
            <p>Refresh the page to try again.</p>
          </OvOvulatoryEventEmptyState>
        </OvOvulatoryEventSurface>
      )}

      {isEmptyRange && (
        <OvOvulatoryEventSurface>
          <OvOvulatoryEventEmptyState title="No scored days in this range">
            <p>
              A day gets a row only when the member has a PdG scan on it, and
              none fall between {formatOvulatoryEventDay(range.day_from)} and{' '}
              {formatOvulatoryEventDay(range.day_to)}. Choose another range
              above.
            </p>
          </OvOvulatoryEventEmptyState>
        </OvOvulatoryEventSurface>
      )}

      {comparisonQuery.isSuccess && !isPastLastPage && total > 0 && (
        <OvOvulatoryEventSurface>
          {predictionsQuery.isError && (
            <StyledAlert severity="warning">
              The stored predictions could not be loaded (
              {predictionsQuery.error.message}), so approximate rule values are
              not marked and day details are unavailable.
            </StyledAlert>
          )}

          {scansQuery.isError && (
            <StyledAlert severity="warning">
              The scan results could not be loaded ({scansQuery.error.message}
              ), so the scan columns are empty.
            </StyledAlert>
          )}

          <OvOvulatoryEventDayTable
            days={buildOvulatoryEventTableDays(rows, scansQuery.data)}
            detailsStatus={toLoadStatus(predictionsQuery)}
            scansStatus={toLoadStatus(scansQuery)}
            total={total}
            offset={pageOffset}
            onPageChange={(page) =>
              setOffset((page - 1) * OVULATORY_EVENT_PAGE_SIZE)
            }
          />
        </OvOvulatoryEventSurface>
      )}
    </Section>
  );
};

export default OvOvulatoryEventComparison;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  min-width: 0;
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem 1.5rem;
  padding: 1rem 1.25rem;
  border-radius: ${Variables.borderRadius.LARGE};
  background: ${Colours.LIGHT_GRAY_BLUE};
`;

const Heading = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.25rem 1.25rem;
`;

const Title = styled.h3`
  margin: 0;
  color: ${Colours.OV_BASE};
  font-size: ${Variables.fontSizes.LARGE};
  font-weight: bold;
`;

const Range = styled.span`
  color: ${Colours.OV_CHARCOAL};
  font-size: ${Variables.fontSizes.MEDIUM};
`;

const Status = styled.p`
  margin: 0.375rem 0 0;
  color: ${Colours.OV_CHARCOAL};
  font-size: ${Variables.fontSizes.SMALL};
`;

const RangeSelector = styled.div`
  display: flex;
  border: 1px solid ${Colours.OV_BORDER_COLOR_V3};
  border-radius: 0.5rem;
  background: ${Colours.WHITE};

  > div {
    min-width: 12rem;
    margin: 0;
  }
`;

const StyledAlert = styled(Alert)`
  && {
    border-radius: ${Variables.borderRadius.LARGE};
  }
`;
